import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  BookMarked, Globe2, Briefcase, FileStack, ShieldCheck, MapPin,
  Award, Users, CheckCircle2, Phone, ArrowRight,
} from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import Marquee from "@/components/Marquee";
import { Reveal, Chapter } from "@/components/Reveal";
import { CTARow, CallButton, GetStartedButton } from "@/components/CTA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SITE, IMAGES } from "@/data/site";
import { SERVICES } from "@/data/services";
import { FAQS } from "@/data/faqs";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-navy" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-y-0 right-0 hidden w-1/2 lg:block">
        <img
          src={IMAGES.hero}
          alt="Passport with international visa stamps"
          className="h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-navy/30" />
      </motion.div>
      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
        <div className="flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-8 inline-flex w-fit flex-col gap-1.5 rounded-lg border border-gold/40 bg-white/5 px-4 py-3 backdrop-blur-sm"
            data-testid="private-service-badge"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold">
              Private Service Provider
            </span>
            <span className="max-w-md text-[11px] leading-snug text-slate-400">
              {SITE.name} is a private company providing passport, visa, and travel document assistance. Not a government office.
            </span>
          </motion.div>
          <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <MaskedLine delay={0.2}>Passport &amp; Visa</MaskedLine>
            <MaskedLine delay={0.32}>Assistance for</MaskedLine>
            <MaskedLine delay={0.44}>
              Your <span className="text-gold">Next Journey</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subcopy"
          >
            Professional assistance for passports, visas, and travel documents for individuals, families, business travelers, executives, and organizations.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          >
            <CTARow light className="mt-9" testPrefix="hero" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-400"
            data-testid="hero-trust-items"
          >
            <span className="flex items-center gap-2"><Award className="h-4 w-4 text-gold" /> 20+ Years of Experience</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" /> Washington, DC Office</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" /> Private &amp; Confidential</span>
          </motion.div>
        </div>
        <motion.div
          id="get-started"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
          className="scroll-mt-28 rounded-2xl bg-white p-6 shadow-2xl shadow-navy-deep/60 sm:p-8"
          data-testid="hero-form-card"
        >
          <div className="gold-rule mb-6 w-16" />
          <h2 className="font-display text-xl font-bold text-navy sm:text-2xl" data-testid="hero-form-heading">
            Tell Us What You Need Help With
          </h2>
          <p className="mb-6 mt-2 text-sm text-slate-500">
            Complete the form and our team will follow up with next steps.
          </p>
          <LeadForm source="home-hero" testId="hero-lead-form" />
        </motion.div>
      </div>
    </section>
  );
};

const TRUST = [
  { icon: Award, title: "20+ Years of Experience", sub: "Assisting U.S. travelers and businesses" },
  { icon: ShieldCheck, title: "Professional Travel Document Assistance", sub: "Passports, visas, apostilles & more" },
  { icon: Users, title: "Individual & Business Travelers", sub: "From family trips to corporate programs" },
  { icon: MapPin, title: "Washington, DC Office", sub: "1629 K St NW, Suite 305" },
];

const TrustStrip = () => (
  <section className="border-b border-slate-100 bg-slate-50" data-testid="trust-strip">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-12 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
      {TRUST.map((t, i) => (
        <Reveal key={t.title} delay={i * 0.08}>
          <div className="flex items-start gap-4" data-testid={`trust-item-${i}`}>
            <t.icon className="mt-1 h-6 w-6 shrink-0 text-gold-dark" />
            <div>
              <p className="font-display text-base font-semibold text-navy">{t.title}</p>
              <p className="mt-1 text-sm text-slate-500">{t.sub}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const CATEGORIES = [
  {
    icon: BookMarked,
    title: "Passport Services",
    desc: "Assistance with new passport applications, renewals, child passports, lost or stolen passports, and urgent travel-document needs.",
    links: ["new-passport", "passport-renewal", "child-passport", "urgent-passport-assistance", "lost-stolen-passport"],
    id: "passport-services",
  },
  {
    icon: Globe2,
    title: "Visa Services",
    desc: "Professional assistance with visa application documentation and requirements for international travel.",
    links: ["visa-assistance", "tourist-visa", "business-visa"],
    id: "visa-services",
  },
  {
    icon: Briefcase,
    title: "Business & Corporate Travel",
    desc: "Assistance for companies, executives, employees, consultants, and other business travelers managing travel-document requirements.",
    links: ["business-corporate-travel", "business-visa"],
    id: "business-services",
  },
  {
    icon: FileStack,
    title: "Travel Document Services",
    desc: "Additional assistance such as apostilles, document authentication, and translation services.",
    links: ["apostille-services", "document-authentication", "translation-services"],
    id: "document-services",
  },
];

const Services = () => (
  <section className="bg-white py-20 lg:py-28" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal>
        <Chapter num="01" label="What We Do" />
        <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl" data-testid="services-heading">
          Travel Document Services, Handled Properly
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div
              id={c.id}
              className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-xl hover:shadow-navy/5"
              data-testid={`service-card-${c.id}`}
            >
              <c.icon className="h-8 w-8 text-gold-dark" />
              <h3 className="mt-5 font-display text-xl font-bold text-navy">{c.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.desc}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {c.links.map((slug) => {
                  const s = SERVICES.find((x) => x.slug === slug);
                  return (
                    <Link
                      key={slug}
                      to={`/services/${slug}`}
                      data-testid={`service-card-link-${slug}`}
                      className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-500 transition-colors hover:border-gold hover:text-navy"
                    >
                      {s.name}
                    </Link>
                  );
                })}
              </div>
              <div className="mt-auto flex items-center gap-4 pt-7">
                <Link
                  to={`/services#${c.id}`}
                  data-testid={`service-learn-more-${c.id}`}
                  className="inline-flex items-center gap-2 font-display text-sm font-semibold text-navy transition-colors hover:text-gold-dark"
                >
                  Learn More <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <GetStartedButton label="Get Started" testId={`service-get-started-${c.id}`} className="!px-5 !py-2.5 !text-xs" />
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section className="bg-slate-50 py-20 lg:py-28" data-testid="about-section">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2">
      <Reveal>
        <div className="relative">
          <div className="absolute -left-4 -top-4 h-24 w-24 border-l-2 border-t-2 border-gold" />
          <img
            src={IMAGES.dc}
            alt="Washington, DC — home of Travel Passport and Visa LLC"
            className="w-full rounded-2xl object-cover shadow-2xl shadow-navy/10"
            loading="lazy"
          />
          <div className="absolute -bottom-6 -right-4 rounded-xl bg-navy px-6 py-4 shadow-xl sm:right-6" data-testid="about-experience-badge">
            <p className="font-display text-3xl font-bold text-gold">20+</p>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-300">Years of Experience</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <Chapter num="02" label="About Us" />
        <h2 className="font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl" data-testid="about-heading">
          About Travel Passport and Visa LLC
        </h2>
        <div className="mt-6 space-y-4 text-base leading-relaxed text-slate-600">
          <p>
            Travel Passport and Visa LLC is a privately owned company with more than <strong className="text-navy">20 years of experience</strong> assisting U.S. travelers and businesses with passport, visa, and travel document services.
          </p>
          <p>
            We help individuals, executives, employees, and organizations navigate documentation requirements associated with domestic and international travel. Our team provides professional assistance with passport applications, renewals, urgent travel needs, visas, and other travel document services.
          </p>
          <p>
            Travel Passport and Visa LLC works with applicable U.S. passport agencies and foreign consulates to assist clients with their documentation needs.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Link
            to="/about"
            data-testid="about-learn-more"
            className="inline-flex items-center gap-2 rounded-full border border-navy/25 px-6 py-3 font-display text-sm font-semibold text-navy transition-colors hover:border-gold hover:text-gold-dark"
          >
            More About Us <ArrowRight className="h-4 w-4" />
          </Link>
          <CallButton testId="about-call-now" />
        </div>
      </Reveal>
    </div>
  </section>
);

const AUDIENCE = ["Businesses", "Executives", "Corporate Travelers", "Employees", "Consultants", "International Teams", "Frequent Travelers"];

const Corporate = () => (
  <section className="grain relative overflow-hidden bg-navy py-20 lg:py-28" data-testid="corporate-section">
    <div className="absolute inset-y-0 right-0 hidden w-2/5 lg:block">
      <img src={IMAGES.executive} alt="Business executive preparing for international travel" className="h-full w-full object-cover opacity-25" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/20" />
    </div>
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal>
        <Chapter num="03" label="For Business" dark />
        <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl" data-testid="corporate-heading">
          Travel Documentation Support for Businesses
        </h2>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
          Keep your employees and business travelers prepared for international travel with professional passport, visa, and travel-document assistance.
        </p>
        <div className="mt-8 flex max-w-2xl flex-wrap gap-2.5" data-testid="corporate-audience">
          {AUDIENCE.map((a) => (
            <span key={a} className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-slate-300">
              {a}
            </span>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <GetStartedButton label="DISCUSS YOUR BUSINESS NEEDS" testId="corporate-discuss-button" />
          <CallButton light testId="corporate-call-now" />
        </div>
      </Reveal>
    </div>
  </section>
);

const STEPS = [
  { n: "1", title: "Tell Us About Your Travel", desc: "Submit the form or call our team." },
  { n: "2", title: "Review Your Requirements", desc: "We help identify the documentation and application requirements relevant to your situation." },
  { n: "3", title: "Prepare Your Documents", desc: "We assist with organizing the required materials and paperwork." },
  { n: "4", title: "Move Forward With Your Application", desc: "We help you navigate the next steps based on the applicable government or consular process." },
];

const Process = () => (
  <section className="bg-white py-20 lg:py-28" data-testid="process-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal>
        <Chapter num="04" label="How It Works" />
        <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl" data-testid="process-heading">
          A Clear Process, From First Call to Application
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.1}>
            <div className="relative" data-testid={`process-step-${s.n}`}>
              <span className="num-outline font-display text-7xl font-bold">{s.n}</span>
              <div className="gold-rule mt-4 w-10" />
              <h3 className="mt-4 font-display text-lg font-bold text-navy">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-14">
          <GetStartedButton testId="process-get-started" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Fees = () => (
  <section className="bg-slate-50 py-20 lg:py-28" data-testid="fees-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal>
        <Chapter num="05" label="Fees & Processing" />
        <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl" data-testid="fees-heading">
          Know What You're Paying For
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
          Government agencies and consulates may charge their own application, processing, or other required fees. Travel Passport and Visa LLC may charge separate professional service fees for assistance.
        </p>
      </Reveal>
      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        <Reveal delay={0.1}>
          <div className="h-full rounded-2xl border border-slate-200 bg-white p-8" data-testid="government-fees-card">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">Government Fees</p>
            <h3 className="mt-2 font-display text-xl font-bold text-navy">Charged by the Government</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Fees charged by the applicable government agency or consulate. These fees are set by the U.S. Department of State or the foreign consulate, are paid to the government, and apply regardless of who helps you prepare your application.
            </p>
            <ul className="mt-5 space-y-2.5">
              {["Set by government agencies and consulates", "Paid to the government", "Required for all applicants"].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" /> {b}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="h-full rounded-2xl border border-gold/50 bg-navy p-8" data-testid="service-fees-card">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Our Service Fees</p>
            <h3 className="mt-2 font-display text-xl font-bold text-white">Charged by Travel Passport and Visa LLC</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Fees charged by Travel Passport and Visa LLC for professional assistance — requirement research, document preparation, application review, and process guidance.
            </p>
            <ul className="mt-5 space-y-2.5">
              {["Explained clearly before any work begins", "Separate from, and in addition to, government fees", "No hidden charges or misleading 'starting at' pricing"].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {b}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.25}>
        <div className="mt-10 rounded-xl border-l-4 border-gold bg-white p-6" data-testid="processing-disclaimer">
          <p className="text-sm font-semibold text-navy">About Processing Times</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{SITE.processingDisclaimer} If you have urgent travel, available options and eligibility depend on the applicable government process — contact us and we'll explain what may apply to your situation.</p>
        </div>
        <div className="mt-8">
          <CTARow testPrefix="fees" />
        </div>
      </Reveal>
    </div>
  </section>
);

const FaqSection = () => (
  <section className="bg-white py-20 lg:py-28" data-testid="faq-section">
    <div className="mx-auto max-w-4xl px-4 sm:px-6">
      <Reveal>
        <Chapter num="06" label="Common Questions" />
        <h2 className="font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl" data-testid="faq-heading">
          Frequently Asked Questions
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
          {FAQS.slice(0, 8).map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-display text-base font-semibold text-navy hover:text-gold-dark">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/faq"
            data-testid="faq-view-all"
            className="inline-flex items-center gap-2 font-display text-sm font-semibold text-navy transition-colors hover:text-gold-dark"
          >
            View All FAQs <ArrowRight className="h-4 w-4" />
          </Link>
          <GetStartedButton testId="faq-get-started" className="!px-5 !py-2.5 !text-xs" />
        </div>
      </Reveal>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="grain relative overflow-hidden bg-navy-deep py-20 lg:py-28" data-testid="final-cta-section">
    <div className="absolute inset-0">
      <img src={IMAGES.airport} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-15" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/60 to-navy-deep" />
    </div>
    <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
      <Reveal>
        <div className="gold-rule mx-auto mb-8 w-24 !bg-none bg-gold" style={{ background: "#D4AF37" }} />
        <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-5xl" data-testid="final-cta-heading">
          Ready to Get Started?
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
          Tell us about your travel plans and the documentation assistance you need. Our team can help you understand the next steps.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <GetStartedButton testId="final-get-started" />
          <a
            href={SITE.phoneTel}
            data-testid="final-call-button"
            className="group inline-flex items-center gap-2.5 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-semibold text-white transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
            CALL {SITE.phoneDisplay}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: SITE.name,
  description: "Private passport, visa, and travel document assistance company in Washington, DC with more than 20 years of experience.",
  telephone: "+1-866-334-8472",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1629 K St NW, Suite 305",
    addressLocality: "Washington",
    addressRegion: "DC",
    postalCode: "20006",
    addressCountry: "US",
  },
};

export default function HomePage() {
  return (
    <>
      <Seo
        title="Passport & Visa Assistance | Travel Passport and Visa LLC — Washington, DC"
        siteName={SITE.siteName}
        description="Private passport, visa, and travel document assistance with 20+ years of experience. Serving individuals, families, and businesses. Call 866-334-8472."
        jsonLd={jsonLd}
      />
      <Hero />
      <Marquee />
      <TrustStrip />
      <Services />
      <About />
      <Corporate />
      <Process />
      <Fees />
      <FaqSection />
      <FinalCTA />
    </>
  );
}
